/**
 * Structural linter for map documents.
 *
 * Run before saving to catch common authoring mistakes:
 * - Unknown asset IDs (object registry miss)
 * - Overlapping trigger volumes of the same kind on the same floor
 * - Floating object instances (Y position far above the floor plane)
 *
 * All checks are non-blocking - issues are returned as an array and the caller
 * decides whether to block the save (only `error` severity does so).
 */
import type { MapDocument, MapObjectInstance } from '../../content/schema/map';
import { getObjectDefinition } from '../../content/registry/objectRegistry';
import { buildFloorYMap } from '../../runtime/mapRuntime';

export type LintSeverity = 'error' | 'warning' | 'info';

export interface LintIssue {
  /** Unique issue type key - stable across runs. */
  code: string;
  severity: LintSeverity;
  message: string;
  /** Instance id if this issue is tied to a specific object. */
  instanceId?: string;
  /** Trigger volume id if this issue is tied to a trigger volume. */
  triggerId?: string;
}

/**
 * Run all linter checks against the map document and its current instance list.
 * The `instances` argument should be the editor's live list (not the source list)
 * so pending additions and modifications are also validated.
 */
export function lintMap(document: MapDocument, instances: MapObjectInstance[]): LintIssue[] {
  const issues: LintIssue[] = [];
  const floorY = buildFloorYMap(document);

  // ── Check 1: Unknown asset IDs ────────────────────────────────────────────
  for (const inst of instances) {
    const def = getObjectDefinition(inst.assetId);
    if (!def) {
      issues.push({
        code: 'UNKNOWN_ASSET',
        severity: 'error',
        message: `Asset "${inst.assetId}" is not registered in the object registry. This instance cannot be rendered.`,
        instanceId: inst.id,
      });
    }
  }

  // ── Check 2: Floating instances ───────────────────────────────────────────
  // An instance is "floating" if its Y position is more than 1.5 m above the
  // floor plane for its declared floor level.
  const FLOAT_THRESHOLD = 1.5;
  for (const inst of instances) {
    const fy = floorY[inst.floor];
    if (fy === undefined) continue; // unknown floor - covered by a different check
    const instY = inst.transform.position[1];
    if (instY > fy + FLOAT_THRESHOLD) {
      issues.push({
        code: 'FLOATING_INSTANCE',
        severity: 'warning',
        message: `Object is ${(instY - fy).toFixed(2)} m above floor ${inst.floor} (Y=${instY.toFixed(2)}, floorY=${fy.toFixed(2)}). It may be floating.`,
        instanceId: inst.id,
      });
    }
  }

  // ── Check 3: Overlapping trigger volumes (same kind, same floor) ──────────
  const triggers = document.triggers ?? [];
  for (let i = 0; i < triggers.length; i++) {
    const a = triggers[i]!;
    for (let j = i + 1; j < triggers.length; j++) {
      const b = triggers[j]!;
      if (a.floor !== b.floor || a.kind !== b.kind) continue;
      // AABB overlap test
      const aHW = a.size[0]! / 2;
      const aHD = a.size[1]! / 2;
      const bHW = b.size[0]! / 2;
      const bHD = b.size[1]! / 2;
      const overlapX = Math.abs(a.center[0]! - b.center[0]!) < aHW + bHW;
      const overlapZ = Math.abs(a.center[1]! - b.center[1]!) < aHD + bHD;
      if (overlapX && overlapZ) {
        issues.push({
          code: 'OVERLAPPING_TRIGGER',
          severity: 'warning',
          message: `Trigger volumes "${a.id}" and "${b.id}" (kind="${a.kind}") overlap on floor ${a.floor}. Player may enter both simultaneously.`,
          triggerId: a.id,
        });
      }
    }
  }

  // ── Check 4: Instances on unknown floors ──────────────────────────────────
  const knownFloors = new Set(document.floors.map((f) => f.level));
  for (const inst of instances) {
    if (!knownFloors.has(inst.floor)) {
      issues.push({
        code: 'UNKNOWN_FLOOR',
        severity: 'error',
        message: `Object is assigned to floor ${inst.floor}, which does not exist in the map document.`,
        instanceId: inst.id,
      });
    }
  }

  // ── Check 5: Sunder spawn / bomb-site completeness ───────────────────
  // Only fires when the map carries at least one spawn marker - maps that
  // have no spawn markers at all are not Sunder maps and are not checked.
  //
  // These are hard `error`s: @sstk/sunder-sim enforces the identical rules at
  // load and REJECTS a map that fails them (typed MapValidationError), so a map
  // that trips any of these would fail to load in the game. Blocking here stops
  // the author before publish rather than after.
  const spawnMarkers = (document.markers ?? []).filter((m) => m.kind === 'spawn');
  if (spawnMarkers.length > 0) {
    const TEAMS = ['raiders', 'wardens'] as const;
    for (const team of TEAMS) {
      const teamSpawns = spawnMarkers.filter((m) => m.metadata?.['team'] === team);
      if (teamSpawns.length === 0) {
        issues.push({
          code: 'SUNDER_MISSING_TEAM_SPAWN',
          severity: 'error',
          message: `Sunder: no spawn markers assigned to "${team}". Add at least one spawn marker with Team = ${team}.`,
        });
      }
    }

    // A spawn marker with an unrecognised or missing team value.
    for (const m of spawnMarkers) {
      const team = m.metadata?.['team'];
      if (team !== 'raiders' && team !== 'wardens') {
        issues.push({
          code: 'SUNDER_SPAWN_UNKNOWN_TEAM',
          severity: 'error',
          message: `Sunder: spawn marker "${m.id}" has no team assigned (metadata.team must be "raiders" or "wardens").`,
          instanceId: m.id,
        });
      }
    }

    // Sunder matches need both objective zones: sites A and B must each exist
    // -- UNLESS this map opts into supportedModes that never read bombSites.
    // 'rift-tactical' is the only mode that uses them (sunder-sim's engine.ts
    // checkStrikeEnd/checkSurgeEnd never touch bombSites), so a map that
    // declares only strike/surge support should not be forced to author two
    // irrelevant trigger volumes purely to pass this check. Omitting
    // supportedModes keeps the historical behaviour: bomb sites required.
    const requiresBombSites =
      document.supportedModes === undefined || document.supportedModes.includes('rift-tactical');
    if (requiresBombSites) {
      const bombSiteTriggers = (document.triggers ?? []).filter((t) => t.kind === 'event');
      const hasSiteA = bombSiteTriggers.some((t) => t.params?.['site'] === 'A');
      const hasSiteB = bombSiteTriggers.some((t) => t.params?.['site'] === 'B');
      if (!hasSiteA || !hasSiteB) {
        issues.push({
          code: 'SUNDER_NO_BOMB_SITES',
          severity: 'error',
          message: !hasSiteA && !hasSiteB
            ? 'Sunder: no bomb sites found. Add Event triggers with Bomb Site = A and Bomb Site = B for objective play.'
            : `Sunder: missing bomb site ${hasSiteA ? 'B' : 'A'}. Both sites A and B must exist for objective play.`,
        });
      }
    }
  }

  return issues;
}
